import { findRoute, type RouteKey } from "@lms/shared";

/**
 * Il client dell'API, verificato a compilazione contro la tabella delle rotte.
 *
 * `RouteKey` e' l'unione dei `"<resource>.<method>"` dichiarati nei bridge:
 * chiamare una rotta che non esiste, o scriverne male il nome, **non
 * compila**. Ed e' vero anche al contrario — quando una rotta sparisce dai
 * bridge e si rigenera la tabella, il codice che la chiamava smette di
 * compilare. E' l'unico pezzo di questo progetto che il "TypeScript ovunque"
 * regala davvero: senza tipi condivisi, un nome di rotta sbagliato diventa un
 * 404 che si scopre in campo.
 *
 * Il corpo delle risposte resta `unknown` e va tipizzato dal chiamante: gli
 * handler ritornano righe di database, e fingere di conoscerne la forma qui
 * sarebbe una promessa che nessuno mantiene.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly route: RouteKey,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** La sessione e' scaduta o non c'e'. Chi chiama deve tornare al login,
   *  non mostrare un errore generico. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Sessione valida, permesso mancante. Diverso da sopra: rifare il login
   *  non serve a niente, e mandarci l'operatore sarebbe una trappola. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export interface ApiOptions {
  /** Iniettabile per i test. Default: la fetch del browser. */
  fetchImpl?: typeof fetch;
  /** Base delle rotte. In sviluppo il proxy di Vite la tiene sulla stessa
   *  origine, cosi' il cookie httpOnly viaggia come in produzione. */
  baseUrl?: string;
  /** Chiamata su ogni 401. E' qui e non nei singoli schermi perche' la
   *  sessione puo' scadere durante QUALSIASI chiamata, e uno schermo che se
   *  ne dimentica lascia l'operatore davanti a una pagina che non si aggiorna
   *  piu' senza dire perche'. */
  onUnauthenticated?: () => void;
}

export class ApiClient {
  private readonly fetchImpl: typeof fetch;
  private readonly baseUrl: string;
  private readonly onUnauthenticated: (() => void) | undefined;

  constructor(opts: ApiOptions = {}) {
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.baseUrl = opts.baseUrl ?? "";
    this.onUnauthenticated = opts.onUnauthenticated;
  }

  async call<T = unknown>(route: RouteKey, body: unknown = {}): Promise<T> {
    const [resource, method] = splitRoute(route);

    // Il controllo a runtime e' ridondante rispetto ai tipi, e resta: la
    // tabella e' la stessa che il server usa per decidere cosa esiste, e
    // fallire QUI con un messaggio chiaro costa meno che leggere un 404 del
    // trasporto. Serve anche a chi arriva da JavaScript non compilato.
    if (!findRoute(resource, method)) {
      throw new ApiError(0, `Rotta non dichiarata: ${route}`, route);
    }

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}/api/${resource}/${method}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        // Il cookie di sessione e' httpOnly: senza questo non parte, e ogni
        // chiamata risponde 401 pur essendo autenticati.
        credentials: "same-origin",
        body: JSON.stringify(body ?? {}),
      });
    } catch (e) {
      // Rete caduta, server giu': non e' un errore HTTP e non ha uno status.
      // Distinguerlo conta — su un sistema di sorveglianza "non riesco a
      // parlare col server" e "il server dice di no" portano a due azioni
      // diverse.
      throw new ApiError(0, e instanceof Error ? e.message : "Server irraggiungibile", route);
    }

    const payload = await readBody(res);

    if (!res.ok) {
      if (res.status === 401) this.onUnauthenticated?.();
      throw new ApiError(res.status, messageOf(payload) ?? `HTTP ${res.status}`, route);
    }
    return payload as T;
  }
}

function splitRoute(route: RouteKey): [string, string] {
  const i = route.indexOf(".");
  return [route.slice(0, i), route.slice(i + 1)];
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (text.length === 0) return {};
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // Un corpo non-JSON su una rotta JSON e' quasi sempre una pagina di
    // errore di un proxy davanti al server. Va detto com'e', non trasformato
    // in "errore sconosciuto".
    return { message: text.slice(0, 200) };
  }
}

function messageOf(payload: unknown): string | undefined {
  if (payload && typeof payload === "object" && "message" in payload) {
    const m = (payload as { message: unknown }).message;
    if (typeof m === "string") return m;
  }
  return undefined;
}
